"use client"

import { useState } from "react"
import { cn } from "@/lib/utils"

const REVIEWS_URL =
  "https://www.google.com/maps/place/Ahead+Health/@47.3734617,8.5311033,17z/data=!4m18!1m9!3m8!1s0x47900b29f69aa4c5:0xec34f49e3ca9ce2e!2sAhead+Health!8m2!3d47.3734618!4d8.5359742!9m1!1b1!16s%2Fg%2F11x1bl_tpp!3m7!1s0x47900b29f69aa4c5:0xec34f49e3ca9ce2e!8m2!3d47.3734618!4d8.5359742!9m1!1b1!16s%2Fg%2F11x1bl_tpp"

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 001 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  )
}

export type GoogleRatingProps = {
  /** "onLight" — dark text + always-visible label, for white/light backgrounds.
   *  "onDark"  — white text + label fades in on hover, for image/dark hero. */
  variant?: "onLight" | "onDark"
  /** Display rating value, e.g. "5". */
  rating?: string
  /** Optional label after the stars, e.g. "based on 200 reviews". */
  reviewLabel?: string
  /** When false, the Google logo is rendered without its chip background. */
  logoChip?: boolean
  /** "md" enlarges the rating label (14px) and tightens the stars. */
  size?: "sm" | "md"
  /** Hide the rating label + dim the stars until hover, then fade in — the
   *  homepage hover animation, usable on light backgrounds too. */
  revealOnHover?: boolean
  /** Resting opacity of the stars in onDark/reveal mode (before hover). Default 0.4. */
  dimmedStarOpacity?: number
  /** onDark, but the stars + label stay fully lit at rest (no hover reveal) —
   *  for a dark hero where the rating should read immediately. */
  staticOnDark?: boolean
  className?: string
}

export function GoogleRating({
  variant = "onLight",
  rating = "5",
  reviewLabel,
  logoChip = true,
  size = "sm",
  revealOnHover = false,
  dimmedStarOpacity = 0.4,
  staticOnDark = false,
  className,
}: GoogleRatingProps) {
  const [hovered, setHovered] = useState(false)
  const onDark = variant === "onDark"
  // Both the dark hero and the opt-in reveal use the hover-lit star pattern —
  // unless staticOnDark forces everything visible at rest.
  const reveal = (onDark || revealOnHover) && !staticOnDark
  const labelToken = size === "md" ? "type-body-md" : "type-body-sm"
  const starGap = size === "md" ? "gap-0" : "gap-px"
  const starSlot = size === "md" ? "size-[14px]" : "size-[16px]"

  return (
    <a
      href={REVIEWS_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "inline-flex items-center gap-2",
        onDark ? "self-center sm:self-auto" : "",
        className
      )}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label={`${rating} stars on Google${reviewLabel ? `, ${reviewLabel}` : ""}`}
    >
      <div
        className={cn(
          "flex size-[24px] flex-shrink-0 items-center justify-center",
          logoChip && "rounded-full",
          logoChip && (onDark ? "bg-white/75 backdrop-blur-sm" : "bg-white")
        )}
      >
        <GoogleIcon />
      </div>
      <div className={cn("flex items-center", starGap)}>
        {Array.from({ length: 5 }, (_, i) => {
          const duration = hovered ? 90 : 65
          const stagger = hovered ? 28 : 20
          const delay = hovered ? i * stagger : (4 - i) * stagger
          // Hover-lit pattern (homepage) for dark + reveal modes; static-on-dark
          // keeps the stars fully lit (only the label is dimmed); otherwise
          // stars stay at full opacity.
          const opacity = reveal ? (hovered ? 1 : dimmedStarOpacity) : 1
          return (
            <div
              key={i}
              className={cn("flex items-center justify-center", starSlot)}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 13.3333 12.68"
                aria-hidden="true"
              >
                <path
                  d="M6.66667 0L8.72667 4.17333L13.3333 4.84667L10 8.09333L10.7867 12.68L6.66667 10.5133L2.54667 12.68L3.33333 8.09333L0 4.84667L4.60667 4.17333L6.66667 0Z"
                  fill="#FBBF24"
                  style={{
                    opacity,
                    transition: reveal
                      ? `opacity ${duration}ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`
                      : undefined,
                  }}
                />
              </svg>
            </div>
          )
        })}
      </div>
      <span
        className={cn(
          labelToken,
          "transition duration-300",
          onDark ? "" : "font-medium text-neutral-900"
        )}
        style={
          onDark
            ? {
                color: staticOnDark
                  ? "rgba(255,255,255,0.55)"
                  : hovered
                    ? "rgba(255,255,255,0.9)"
                    : "rgba(255,255,255,0)",
                transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
                transform: "translateY(1px)",
              }
            : revealOnHover
              ? {
                  opacity: hovered ? 1 : 0,
                  transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
                }
              : undefined
        }
      >
        {rating}
        {reviewLabel ? (
          <span
            className={cn(
              "ml-1.5 font-normal",
              onDark ? "" : "text-neutral-500"
            )}
          >
            {reviewLabel}
          </span>
        ) : null}
      </span>
    </a>
  )
}
